// ─── Machine-readable error codes on a failed API response ───────────────────
// The `code` field of an error body (`{ success: false, error, code, statusCode }`).
// Shared so the API, the SPA and the error store agree on ONE spelling — a code
// typed out twice silently stops matching the day one copy is edited.
/** The acting user hasn't connected their own account for an integration (412). */
export const ERR_CONNECTION_REQUIRED = 'connection_required';
/** The acting user lacks a PCP RBAC permission (403). */
export const ERR_PERMISSION_DENIED = 'permission_denied';
/** The acting user's OWN connected Azure DevOps account was refused by ADO (403).
 *  Their ADO access is the problem, not PCP — they must ask IT support for it. */
export const ERR_ADO_PERMISSION_DENIED = 'ado_permission_denied';
/** Azure DevOps refused a work-item write because someone else saved the item at
 *  the same moment — ADO's own optimistic-concurrency check, TF26071 (409). The
 *  write is retried first; this code only escapes when the conflict survives. */
export const ERR_ADO_REVISION_CONFLICT = 'ado_revision_conflict';
/** The Azure DevOps work item a write targeted is gone from ADO (TF401232, 404).
 *  Confirmed with the service account and dropped from the mirror, so the item
 *  disappears from PCP too and there is nothing left to retry (410). */
export const ERR_WORK_ITEM_GONE = 'work_item_gone';
/** A Kanban board move did not put the card in the column / Doing-Done side that
 *  was asked for (502). Usually the move WAS written and Azure DevOps placed the
 *  card elsewhere — the response then carries `data`, where the card is now. With
 *  no `data`, nothing was written (the board named no field to write the move to).
 *  Deliberately NOT user-actionable: a card sitting where nobody chose, or a board
 *  that cannot be written, is a fault worth a Diagnostics row (b-01999). */
export const ERR_ADO_BOARD_MOVE_NOT_LANDED = 'ado_board_move_not_landed';
/**
 * Rejections that are EXPECTED and USER-ACTIONABLE: the request was refused by
 * design, the user is told exactly what to do, and nothing is broken. These are
 * deliberately kept OUT of the unified app_errors store (server tee in
 * server.ts, browser tee in web/src/lib/submitError.ts) so Diagnostics and the
 * list_app_errors MCP tool stay a list of things that are actually WRONG.
 *
 * The bar for adding a code here: a competent user reading the message knows the
 * next step, and no amount of PCP code change would have prevented it. An error
 * that needs a developer to fix does NOT belong here.
 */
export const USER_ACTIONABLE_ERROR_CODES = [
    ERR_CONNECTION_REQUIRED,
    ERR_PERMISSION_DENIED,
    ERR_ADO_PERMISSION_DENIED,
    ERR_ADO_REVISION_CONFLICT,
    ERR_WORK_ITEM_GONE,
];
/** True when an error `code` marks an expected, user-actionable rejection that
 *  must not be recorded as an application error. */
export function isUserActionableErrorCode(code) {
    return typeof code === 'string' && USER_ACTIONABLE_ERROR_CODES.includes(code);
}
//# sourceMappingURL=error-codes.js.map